import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import { useCallback, useEffect, useRef } from "react";

export enum CanvasKeyboardShortcut {
  delete = "delete; backspace",
  enter = "enter",
  esc = "escape",
  space = "space",
  undo = "meta+z",
  redo = "meta+shift+z; meta+y; f4",
  selectAll = "meta+a",
  zoomIn = "meta+=",
  zoomOut = "meta+-",
  resetZoom = "meta+0",
  zoomToFit = "alt+1",
  zoomToFitSelection = "alt+2",

  left = "ArrowLeft; shift+ArrowLeft",
  right = "ArrowRight; shift+ArrowRight",
  up = "ArrowUp; shift+ArrowUp",
  down = "ArrowDown; shift+ArrowDown",

  moveTool = "v; h;",

  duplicate = "meta+d",
}

const AllowedRepeat = new Set<CanvasKeyboardShortcut>([
  CanvasKeyboardShortcut.left,
  CanvasKeyboardShortcut.right,
  CanvasKeyboardShortcut.up,
  CanvasKeyboardShortcut.down,
]);

const MODIFIERS = new Set(["meta", "shift", "control", "alt"]);

const ArrowKeys: { [key: string]: number } = {
  ArrowLeft: 1,
  ArrowRight: 2,
  ArrowUp: 4,
  ArrowDown: 8,
};

export function setKey(bitmap: number, key: string): number {
  if (key in ArrowKeys) {
    return bitmap | ArrowKeys[key];
  }
  return bitmap;
}

export function unsetKey(bitmap: number, key: string): number {
  if (key in ArrowKeys) {
    return bitmap & ~ArrowKeys[key];
  }
  return bitmap;
}

export function arrowKeysToVector(bitmap: number): [number, number] {
  let x = 0,
    y = 0;
  if (bitmap & ArrowKeys.ArrowLeft) x -= 1;
  if (bitmap & ArrowKeys.ArrowRight) x += 1;
  if (bitmap & ArrowKeys.ArrowUp) y -= 1;
  if (bitmap & ArrowKeys.ArrowDown) y += 1;
  return [x, y];
}

function isMatchingPattern(pattern: string, e: KeyboardEvent, key: string) {
  let keys = pattern.toLowerCase().split("+");
  const modifiersMatch =
    keys.includes("meta") == e.metaKey &&
    keys.includes("shift") == e.shiftKey &&
    keys.includes("control") == e.ctrlKey &&
    keys.includes("alt") == e.altKey;

  if (!modifiersMatch) return false;

  keys = keys.filter((key) => !MODIFIERS.has(key));

  // Special handling for Alt/Option + number on Mac
  if (isMac() && e.altKey && keys.length > 0 && /^\d$/.test(keys[0])) {
    const expectedKey = keys[0];
    const physicalKey = e.code.toLowerCase();
    return physicalKey === `digit${expectedKey}`;
  }

  return (keys.length === 0 && MODIFIERS.has(key)) || (keys.length > 0 && keys.includes(key));
}

function isKeyCommand(pattern: string, e: KeyboardEvent, key: string) {
  if (!navigator.userAgent.includes("Macintosh")) pattern = pattern.replaceAll("meta", "control");
  const patterns = pattern.replace(" ", "").split(";");
  for (const pattern of patterns) {
    if (isMatchingPattern(pattern, e, key)) {
      return true;
    }
  }
  return false;
}

// Check the keyboard event against the shortcut list
function findShortcut(e: KeyboardEvent): CanvasKeyboardShortcut | null {
  if (!e.key) return null;

  let key = e.key.toLowerCase();
  if (key == " ") key = "space";

  for (const shortcut of Object.values(CanvasKeyboardShortcut)) {
    if (isKeyCommand(shortcut, e, key)) {
      return shortcut;
    }
  }
  return null;
}

// We ignore keyboard shortcut if the target is an html text input element of any kind
function shouldIgnoreShortcut(): boolean {
  const activeElement = document.activeElement;
  if (activeElement) {
    return activeElement.tagName !== "BODY";
  }
  return false;
}

type KeyboardShortcutHookOptions = {
  enabled: boolean;
  capture: boolean;
  disablePropagation: boolean;
};

export function useKeyboardShortcuts(
  onKeyboardShortcut: (shortcut: CanvasKeyboardShortcut, e: KeyboardEvent, keyDown: boolean) => void,
  options?: KeyboardShortcutHookOptions
) {
  const { capture = false, enabled = true, disablePropagation = true } = options || {};

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (shouldIgnoreShortcut()) return;
      const shortcut = findShortcut(e);
      if (shortcut) {
        disablePropagation && e.preventDefault(); // Prevent the browser from doing the shortcut
        // Make sure shortcuts run once
        if (!e.repeat) {
          onKeyboardShortcut(shortcut, e, true);
        }
      }
    },
    [disablePropagation, onKeyboardShortcut]
  );

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener("keydown", handleKeyDown, capture);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [capture, enabled, handleKeyDown]);
}

export function useHoldKeys(
  keys: CanvasKeyboardShortcut | CanvasKeyboardShortcut[],
  onKeyDown: (shortcut: CanvasKeyboardShortcut, e: KeyboardEvent) => void,
  onKeyUp: (shortcut: CanvasKeyboardShortcut, e: KeyboardEvent) => void,
  options?: KeyboardShortcutHookOptions
) {
  const { capture = false, enabled = true, disablePropagation = true } = options || {};
  const keysArray = useDeepEqualMemo(Array.isArray(keys) ? keys : [keys]);
  const heldKeys = useRef<Set<CanvasKeyboardShortcut>>(new Set());

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (shouldIgnoreShortcut()) return;

      const shortcut = findShortcut(e);
      if (shortcut && keysArray.includes(shortcut)) {
        disablePropagation && e.preventDefault();
        const wasAlreadyHeld = heldKeys.current.has(shortcut);
        heldKeys.current.add(shortcut);

        if (!wasAlreadyHeld || AllowedRepeat.has(shortcut)) {
          onKeyDown(shortcut, e);
        }
      }
    },
    [disablePropagation, keysArray, onKeyDown]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      const shortcut = findShortcut(e);
      if (shortcut && keysArray.includes(shortcut)) {
        const wasHeld = heldKeys.current.has(shortcut);
        if (wasHeld) {
          heldKeys.current.delete(shortcut);
        }
        // Always trigger onKeyUp for keys in our array, regardless of held state
        // This ensures quick key presses are properly handled
        onKeyUp(shortcut, e);
      }
    },
    [keysArray, onKeyUp]
  );

  useEffect(() => {
    if (!enabled) return;

    // Ensure the cleanup function uses the same heldKeys reference
    const currentHeldKeys = heldKeys.current;

    window.addEventListener("keydown", handleKeyDown, capture);
    window.addEventListener("keyup", handleKeyUp, capture);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, capture);
      window.removeEventListener("keyup", handleKeyUp, capture);

      // Clear any held keys when unmounting
      currentHeldKeys.clear();
    };
  }, [capture, enabled, handleKeyDown, handleKeyUp]);
}

export interface ModifiersState {
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
  ctrlOrCmd: boolean;
}

export function useModifierKeys() {
  const modifiers = useRef<ModifiersState>({
    shift: false,
    alt: false,
    meta: false,
    ctrl: false,
    ctrlOrCmd: false,
  });
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      modifiers.current.shift = e.shiftKey;
      modifiers.current.alt = e.altKey;
      modifiers.current.meta = e.metaKey;
      modifiers.current.ctrl = e.ctrlKey;
      modifiers.current.ctrlOrCmd = isMac() ? e.metaKey : e.ctrlKey;
    }

    window.addEventListener("keydown", handleKey);
    window.addEventListener("keyup", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keyup", handleKey);
    };
  }, []);
  return modifiers.current;
}

export function isMac() {
  return /mac/i.test((navigator as any).userAgentData ? (navigator as any).userAgentData.platform : navigator.platform);
}

export function isWindows() {
  return /win/i.test((navigator as any).userAgentData ? (navigator as any).userAgentData.platform : navigator.platform);
}
